import { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Card, CardContent, Box, Typography, TextField, Button } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { EntityPicker } from './EntityPicker';
import type { AnchorType, InvestigationInput } from './types';

interface SymptomFormProps {
  onRun: (input: InvestigationInput) => void;
  // True while a run is in flight — disables the form and the Run button.
  running: boolean;
}

// Symptom multiline TextField + EntityPicker + "Run investigation" button (REF-05 s.1).
// The button is disabled while a run is in flight, when symptomText is blank, or when an
// anchorType is selected without an anchorId (client-side mirror of the VALIDATION_FAILED guard).
export const SymptomForm = ({ onRun, running }: SymptomFormProps) => {
  const translate = useTranslate();
  const [symptomText, setSymptomText] = useState('');
  const [anchorType, setAnchorType] = useState<AnchorType>('none');
  const [anchorId, setAnchorId] = useState('');

  const symptomBlank = symptomText.trim() === '';
  const anchorIdMissing = anchorType !== 'none' && anchorId.trim() === '';
  const canRun = !running && !symptomBlank && !anchorIdMissing;

  const handleRun = () => {
    if (!canRun) return;
    onRun({
      symptomText: symptomText.trim(),
      anchorType,
      anchorId: anchorType === 'none' ? null : anchorId.trim(),
    });
  };

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 } }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {translate('aiTroubleshooting.form.title')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={translate('aiTroubleshooting.form.symptom')}
            placeholder={translate('aiTroubleshooting.form.symptomPlaceholder')}
            value={symptomText}
            onChange={(e) => setSymptomText(e.target.value)}
            disabled={running}
          />

          <EntityPicker
            anchorType={anchorType}
            anchorId={anchorId}
            onChange={({ anchorType: t, anchorId: id }) => {
              setAnchorType(t);
              setAnchorId(id);
            }}
            disabled={running}
          />
        </Box>

        <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrow />}
            onClick={handleRun}
            disabled={!canRun}
          >
            {translate('aiTroubleshooting.form.run')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SymptomForm;
